import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../../components/ui/Layout';
import ConfirmationModal from '../../components/ui/ConfirmationModal';


import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import BillForm from './components/BillForm';
import BillCard from './components/BillCard';
import BillCalendar from './components/BillCalendar';
import PaymentHistory from './components/PaymentHistory';

import { Checkbox } from '../../components/ui/Checkbox';
import { useLongPress } from '../../hooks/useGestures';
import { format, addDays, isBefore, isAfter } from 'date-fns';
import { CalendarSkeleton, CardSkeleton } from '../../components/ui/LoadingSkeleton';
import EmptyState from '../../components/ui/EmptyState';
import ErrorMessage from '../../components/ui/ErrorMessage';

const RecurringBillTracker = () => {
  const { user } = useAuth();
  const [bills, setBills] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBillForm, setShowBillForm] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [activeView, setActiveView] = useState('list'); // list, calendar, history
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [favorites, setFavorites] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedBills, setSelectedBills] = useState(new Set());
  const [errors, setErrors] = useState([]);
  const [showDeleteBillConfirm, setShowDeleteBillConfirm] = useState(false);
  const [billToDelete, setBillToDelete] = useState(null);
  const [deleteBillLoading, setDeleteBillLoading] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  useEffect(() => {
    if (user) {
      fetchBills();
      fetchPaymentHistory();
    }
  }, [user]);

  const fetchBills = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase?.from('recurring_bills')?.select('*')?.eq('user_id', user?.id)?.eq('is_active', true)?.order('next_due_date', { ascending: true });

      if (error) throw error;
      setBills(data || []);
      // Initialize favorites from fetched data
      const favoriteBills = new Set(data?.filter(b => b?.is_favorite)?.map(b => b?.id) || []);
      setFavorites(favoriteBills);
    } catch (error) {
      console.error('Error fetching bills:', error);
      toast?.error('Failed to load bills');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const { data, error } = await supabase?.from('bill_payment_history')?.select('*, recurring_bills(bill_name, category)')?.eq('user_id', user?.id)?.order('payment_date', { ascending: false })?.limit(50);

      if (error) throw error;
      setPaymentHistory(data || []);
    } catch (error) {
      console.error('Error fetching payment history:', error);
    }
  };

  const handleAddBill = () => {
    setEditingBill(null);
    setShowBillForm(true);
  };

  const handleEditBill = (bill) => {
    setEditingBill(bill);
    setShowBillForm(true);
  };

  const handleDeleteBill = async (billId) => {
    const bill = bills?.find(b => b?.id === billId);
    setBillToDelete(bill);
    setShowDeleteBillConfirm(true);
  };

  const confirmDeleteBill = async () => {
    if (!billToDelete) return;
    setDeleteBillLoading(true);
    try {
      const { error } = await supabase?.from('recurring_bills')?.delete()?.eq('id', billToDelete?.id)?.eq('user_id', user?.id);
      if (error) throw error;
      toast?.success('Bill deleted successfully');
      fetchBills();
      setShowDeleteBillConfirm(false);
      setBillToDelete(null);
    } catch (error) {
      console.error('Error deleting bill:', error);
      toast?.error('Failed to delete bill');
    } finally {
      setDeleteBillLoading(false);
    }
  };

  const handleToggleFavorite = (billId, newStatus) => {
    setFavorites(prev => {
      const newSet = new Set(prev);
      if (newStatus) {
        newSet?.add(billId);
      } else {
        newSet?.delete(billId);
      }
      return newSet;
    });
  };

  const handleLongPress = () => {
    setSelectionMode(true);
  };

  const handleToggleSelection = (billId) => {
    setSelectedBills(prev => {
      const newSet = new Set(prev);
      if (newSet?.has(billId)) {
        newSet?.delete(billId);
      } else {
        newSet?.add(billId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setSelectedBills(new Set(bills?.map(b => b?.id)));
  };

  const handleDeselectAll = () => {
    setSelectedBills(new Set());
  };

  const handleCancelSelection = () => {
    setSelectionMode(false);
    setSelectedBills(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedBills?.size === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    try {
      const idsToDelete = Array.from(selectedBills);
      const { error } = await supabase
        ?.from('recurring_bills')
        ?.delete()
        ?.in('id', idsToDelete);

      if (error) throw error;

      toast?.success(`Deleted ${selectedBills?.size} bills`);
      fetchBills();
      handleCancelSelection();
      setShowBulkDeleteConfirm(false);
    } catch (error) {
      console.error('Error bulk deleting:', error);
      toast?.error('Failed to delete bills');
    }
  };

  const handleBulkMarkPaid = async () => {
    if (selectedBills?.size === 0) return;

    try {
      const idsToUpdate = Array.from(selectedBills);
      const today = new Date()?.toISOString()?.split('T')?.[0];

      // Create payment history records
      const paymentRecords = idsToUpdate?.map(billId => {
        const bill = bills?.find(b => b?.id === billId);
        return {
          bill_id: billId,
          user_id: user?.id,
          payment_date: today,
          amount_paid: bill?.amount,
          status: 'paid',
          payment_method: 'Manual'
        };
      });

      const { error: historyError } = await supabase
        ?.from('bill_payment_history')
        ?.insert(paymentRecords);

      if (historyError) throw historyError;

      // Update bills' last payment date
      const { error: updateError } = await supabase
        ?.from('recurring_bills')
        ?.update({ last_payment_date: today })
        ?.in('id', idsToUpdate);

      if (updateError) throw updateError;

      toast?.success(`Marked ${selectedBills?.size} bills as paid`);
      fetchBills();
      fetchPaymentHistory();
      handleCancelSelection();
    } catch (error) {
      console.error('Error bulk marking as paid:', error);
      toast?.error('Failed to mark bills as paid');
    }
  };

  const longPressHandlers = useLongPress(handleLongPress);

  const retryError = (errorId) => {
    // Implement retry logic based on error type
    const error = errors?.find(e => e?.id === errorId);
    if (error?.type === 'fetch_bills') {
      fetchBills();
    } else if (error?.type === 'fetch_payment_history') {
      fetchPaymentHistory();
    }
  };

  const removeError = (errorId) => {
    setErrors(prev => prev?.filter(e => e?.id !== errorId));
  };

  const handleMarkAsPaid = async (bill) => {
    try {
      // Create payment history record
      const { error: historyError } = await supabase?.from('bill_payment_history')?.insert({
          bill_id: bill?.id,
          user_id: user?.id,
          payment_date: new Date()?.toISOString()?.split('T')?.[0],
          amount_paid: bill?.amount,
          status: 'paid',
          payment_method: 'Manual'
        });

      if (historyError) throw historyError;

      // Update bill's last payment date
      const { error: updateError } = await supabase?.from('recurring_bills')?.update({
          last_payment_date: new Date()?.toISOString()?.split('T')?.[0]
        })?.eq('id', bill?.id)?.eq('user_id', user?.id);

      if (updateError) throw updateError;

      toast?.success('Bill marked as paid');
      fetchBills();
      fetchPaymentHistory();
    } catch (error) {
      console.error('Error marking bill as paid:', error);
      toast?.error('Failed to mark bill as paid');
    }
  };

  const handleSendReminder = async (bill) => {
    try {
      // Call Supabase Edge Function to send email reminder
      const { error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'bill_reminder',
          to: user?.email,
          data: {
            billName: bill?.bill_name,
            amount: bill?.amount,
            dueDate: format(new Date(bill?.next_due_date), 'MMMM dd, yyyy'),
            category: bill?.category
          }
        }
      });

      if (error) throw error;
      toast?.success('Reminder sent successfully');
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast?.error('Failed to send reminder');
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editingBill) {
        // Update existing bill
        const { error } = await supabase?.from('recurring_bills')?.update({
            bill_name: formData?.billName,
            amount: formData?.amount,
            frequency: formData?.frequency,
            due_date: formData?.dueDate,
            category: formData?.category,
            autopay_enabled: formData?.autopayEnabled,
            reminder_days: formData?.reminderDays,
            notes: formData?.notes
          })?.eq('id', editingBill?.id)?.eq('user_id', user?.id);

        if (error) throw error;
        toast?.success('Bill updated successfully');
      } else {
        // Create new bill
        const { error } = await supabase?.from('recurring_bills')?.insert({
            user_id: user?.id,
            bill_name: formData?.billName,
            amount: formData?.amount,
            frequency: formData?.frequency,
            due_date: formData?.dueDate,
            category: formData?.category,
            autopay_enabled: formData?.autopayEnabled,
            reminder_days: formData?.reminderDays,
            notes: formData?.notes,
            next_due_date: formData?.dueDate
          });

        if (error) throw error;
        toast?.success('Bill added successfully');
      }

      setShowBillForm(false);
      setEditingBill(null);
      fetchBills();
    } catch (error) {
      console.error('Error saving bill:', error);
      toast?.error('Failed to save bill');
    }
  };

  const upcomingBills = useMemo(() => {
    const today = new Date();
    const next7Days = addDays(today, 7);
    return bills?.filter(bill => {
      const dueDate = new Date(bill?.next_due_date);
      return isAfter(dueDate, today) && isBefore(dueDate, next7Days);
    });
  }, [bills]);

  const overdueBills = useMemo(() => {
    const today = new Date();
    return bills?.filter(bill => {
      const dueDate = new Date(bill?.next_due_date);
      return isBefore(dueDate, today);
    });
  }, [bills]);

  const monthlyTotal = useMemo(() => {
    return bills?.reduce((total, bill) => {
      let monthlyAmount = 0;
      switch (bill?.frequency) {
        case 'weekly':
          monthlyAmount = bill?.amount * 4.33;
          break;
        case 'biweekly':
          monthlyAmount = bill?.amount * 2.17;
          break;
        case 'monthly':
          monthlyAmount = bill?.amount;
          break;
        case 'quarterly':
          monthlyAmount = bill?.amount / 3;
          break;
        case 'yearly':
          monthlyAmount = bill?.amount / 12;
          break;
        default:
          monthlyAmount = 0;
      }
      return total + monthlyAmount;
    }, 0);
  }, [bills]);

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-6 md:py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading bills...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Recurring Bill Tracker</h1>
            <p className="text-gray-600 mt-1">Manage and track your recurring bills and subscriptions</p>
          </div>

          {/* Error Messages */}
          {errors?.length > 0 && (
            <div className="space-y-3">
              {errors?.map((error) => (
                <ErrorMessage
                  key={error?.id}
                  error={error}
                  onRetry={retryError}
                  onDismiss={removeError}
                />
              ))}
            </div>
          )}

          {loading ? (
            <>
              {activeView === 'list' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CardSkeleton />
                  <CardSkeleton />
                </div>
              )}

              {activeView === 'calendar' && <CalendarSkeleton />}

              {activeView === 'history' && <PaymentHistory paymentHistory={paymentHistory} loading={loading} />}
            </>
          ) : bills?.length === 0 ? (
            <EmptyState
              icon="Receipt"
              title="No Recurring Bills Tracked"
              description="Start managing your recurring expenses by adding bills like rent, utilities, subscriptions, and insurance. Never miss a payment again."
              actionLabel="Add Your First Bill"
              onAction={handleAddBill}
              secondaryActionLabel="Learn More"
              onSecondaryAction={() => window.open('https://www.investopedia.com/managing-recurring-bills', '_blank')}
              size="default"
              className=""
            />
          ) : (
            <>
              {activeView === 'list' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {bills?.map((bill) => {
                    const isOverdue = new Date(bill?.next_due_date) < new Date();
                    return (
                      <div key={bill?.id} className="relative" {...(selectionMode ? {} : longPressHandlers)}>
                        {selectionMode && (
                          <div className="absolute top-4 left-4 z-10">
                            <Checkbox
                              checked={selectedBills?.has(bill?.id)}
                              onChange={() => handleToggleSelection(bill?.id)}
                            />
                          </div>
                        )}
                        <div className={selectionMode ? 'pl-8' : ''}>
                          <BillCard
                            bill={bill}
                            onEdit={handleEditBill}
                            onDelete={handleDeleteBill}
                            onMarkPaid={handleMarkAsPaid}
                            onSendReminder={handleSendReminder}
                            isOverdue={isOverdue}
                            isFavorite={favorites?.has(bill?.id)}
                            onToggleFavorite={handleToggleFavorite}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {activeView === 'calendar' && (
                <BillCalendar
                  bills={bills}
                  selectedMonth={selectedMonth}
                  onMonthChange={setSelectedMonth}
                  onBillClick={handleEditBill}
                />
              )}

              {activeView === 'history' && (
                <PaymentHistory
                  paymentHistory={paymentHistory}
                  loading={loading}
                />
              )}
            </>
          )}

          {/* Bill Form Modal */}
          {showBillForm && (
            <BillForm
              bill={editingBill}
              onSubmit={handleFormSubmit}
              onClose={() => {
                setShowBillForm(false);
                setEditingBill(null);
              }}
              existingBills={bills}
            />
          )}

          {/* Delete Bill Confirmation Modal */}
          <ConfirmationModal
            isOpen={showDeleteBillConfirm}
            onClose={() => {
              setShowDeleteBillConfirm(false);
              setBillToDelete(null);
            }}
            onConfirm={confirmDeleteBill}
            title="Delete Bill"
            message={`Are you sure you want to delete "${billToDelete?.bill_name || 'this bill'}"?`}
            warningMessage="This will permanently remove the bill and all its payment history. This action cannot be undone."
            itemDetails={billToDelete ? {
              name: billToDelete?.bill_name || 'N/A',
              amount: `$${parseFloat(billToDelete?.amount || 0)?.toLocaleString('en-CA', { minimumFractionDigits: 2 })}`,
              frequency: billToDelete?.frequency || 'N/A',
              category: billToDelete?.category || 'N/A'
            } : null}
            confirmLabel="Delete Bill"
            cancelLabel="Cancel"
            isLoading={deleteBillLoading}
          />

          {/* Bulk Delete Bills Confirmation Modal */}
          <ConfirmationModal
            isOpen={showBulkDeleteConfirm}
            onClose={() => setShowBulkDeleteConfirm(false)}
            onConfirm={confirmBulkDelete}
            title="Delete Multiple Bills"
            message={`Are you sure you want to delete ${selectedBills?.size} bill${selectedBills?.size > 1 ? 's' : ''}?`}
            warningMessage="This will permanently remove all selected bills and their payment history. This action cannot be undone."
            confirmLabel={`Delete ${selectedBills?.size} Bill${selectedBills?.size > 1 ? 's' : ''}`}
            cancelLabel="Cancel"
          />
        </div>
      </div>
    </Layout>
  );
};

export default RecurringBillTracker;